import { MoreHorizRounded, RefreshRounded } from '@mui/icons-material';
import { Box, Button, IconButton, Popover, Stack, Tooltip, Typography, useMediaQuery, useTheme, type ButtonProps } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type MouseEvent, type ReactElement, type ReactNode, type Ref } from 'react';
import { designTokens } from '../tokens';
import { createFocusRingStyles } from '../interaction';
import { popupSurfaceStyles } from '../popupSurface';
import { pageContentFrame, type PageContentWidth } from './pageContent';
import { usePageHeaderActionsPresentation } from './PageHeaderActionsContext';
import { usePageHeaderIcon } from './PageHeaderIconContext';
import { PageTabsProvider } from './PageTabsContext';
import { useHeaderActionOverflow } from './useHeaderActionOverflow';
import { HelpPopover } from './HelpPopover';

export type { PageContentWidth } from './pageContent';

export type PageRefreshAction = {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export type PageHeaderAction = {
  id: string;
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  color?: ButtonProps['color'];
};

function HeaderActionButton({ item, inline = false, onFocusRemoved }: { item: PageHeaderAction; inline?: boolean; onFocusRemoved: (id: string) => void }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const button = buttonRef.current;
    return () => { if (button && document.activeElement === button) onFocusRemoved(item.id); };
  }, [item.id, onFocusRemoved]);
  return <Button ref={buttonRef} data-header-action-id={item.id} variant={inline ? 'weak' : 'text'} startIcon={item.icon} color={item.color} disabled={item.disabled} loading={item.loading} onClick={item.onClick}>{item.label}</Button>;
}

export type PageHeaderProps = {
  title: ReactNode;
  /** 선택적 설명을 제목 옆 도움말 버튼에서 엽니다. */
  help?: ReactNode;
  /** 제목 크기를 따르는 장식 아이콘입니다. null은 기본 아이콘을 숨기며 navigation이 우선합니다. */
  icon?: ReactNode;
  /**
   * title이 편집 control 같은 slot일 때 heading의 안정적인 접근 가능한 이름입니다.
   * 문자열 title은 별도 label 없이 자체 text를 사용합니다.
   */
  titleLabel?: string;
  /** 긴 문서 제목의 영역을 넓힙니다. 좁은 화면에서는 탭·작업보다 위에 한 행으로 표시합니다. */
  titleWidth?: 'standard' | 'wide';
  titleRef?: Ref<HTMLHeadingElement>;
  /** 페이지 보기 전환. 넓은 화면은 제목 옆, 좁은 화면은 제목과 작업 아래에 표시합니다. */
  tabs?: ReactNode;
  /** 페이지 조회 갱신. desktop 제목 옆에 표시하고 mobile에서는 작업 메뉴에 배치합니다. */
  refreshAction?: PageRefreshAction;
  navigation?: ReactNode;
  /** 항상 표시할 조회 도구입니다. 탭이 없으면 제목 옆에서 구분선으로 나누며, 좁은 화면이나 탭 아래에서는 다음 줄을 채웁니다. */
  controls?: ReactNode;
  utilities?: ReactNode;
  /** 단일 대표 버튼입니다. 표시 위치는 primaryActionVisibility를 따릅니다. */
  primaryAction?: ReactElement<ButtonProps>;
  /** 기본 responsive는 mobile 작업 메뉴로 옮기고, always는 제목 옆에 계속 표시합니다. */
  primaryActionVisibility?: 'responsive' | 'always';
  actions?: ReactNode;
  /** 중요도 순의 버튼 작업. 대표 행동을 포함해 최대 세 개를 표시하고 남은 폭에 따라 메뉴로 옮깁니다. */
  actionItems?: readonly PageHeaderAction[];
  /** 닫기처럼 항상 보여야 하는 작업 그룹은 좁은 화면에서도 메뉴로 옮기지 않습니다. */
  actionsVisibility?: 'responsive' | 'always';
  actionsLabel?: string;
  /** 편집의 저장·취소입니다. 일반 작업 메뉴와 별도로 항상 표시합니다. */
  editActions?: ReactNode;
  contentWidth?: PageContentWidth;
  prominent?: boolean;
};

export function PageHeader({ title, help, icon, titleLabel, titleWidth = 'standard', titleRef, tabs, refreshAction, navigation, controls, utilities, primaryAction, primaryActionVisibility = 'responsive', actions, actionItems = [], actionsVisibility = 'responsive', actionsLabel = '페이지 작업', editActions, contentWidth = 'wide', prominent = false }: PageHeaderProps) {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const actionsPresentation = usePageHeaderActionsPresentation();
  const defaultIcon = usePageHeaderIcon();
  const plainTitle = typeof title === 'string' || typeof title === 'number';
  // 복합 제목은 자체 leading 슬롯을 소유하며 뒤로가기 옆에는 장식을 중복하지 않습니다.
  const titleIcon = navigation ? null : icon === undefined ? (plainTitle ? defaultIcon : null) : icon;
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const menuActionsRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const displacedFocus = useRef<string | null>(null);
  const focusFrame = useRef(0);
  const onActionFocusRemoved = useCallback((id: string) => { displacedFocus.current = id; }, []);
  const actionsPopoverId = useId();
  const hasHeaderTools = Boolean(tabs || controls);
  const { headerRef, measurementRef, visibleCount } = useHeaderActionOverflow(Boolean(actionItems.length) && !mobile, Boolean(primaryAction), hasHeaderTools);
  const inlineItemCount = actionsVisibility === 'always' ? actionItems.length : mobile ? 0 : visibleCount;
  const inlineItems = actionItems.slice(0, inlineItemCount);
  const overflowItems = actionItems.slice(inlineItemCount);
  const actionsInMenu = actionsVisibility !== 'always' && (mobile || actionsPresentation === 'menu');
  const primaryActionInMenu = mobile && primaryActionVisibility === 'responsive';
  const hasMenuContent = Boolean((primaryActionInMenu && primaryAction) || (mobile && (utilities || refreshAction)) || (actionsInMenu && actions) || overflowItems.length);
  const actionsOpen = hasMenuContent && Boolean(actionsAnchor);
  useLayoutEffect(() => {
    if (!displacedFocus.current) return;
    const candidates = [
      ...Array.from(headerRef.current?.querySelectorAll<HTMLButtonElement>('[data-header-action-id]') ?? []),
      ...(actionsOpen ? Array.from(menuActionsRef.current?.querySelectorAll<HTMLButtonElement>('[data-header-action-id]') ?? []) : []),
    ];
    const replacement = candidates.find(button => button.dataset.headerActionId === displacedFocus.current && !button.disabled);
    if (replacement && actionsOpen && headerRef.current?.contains(replacement)) {
      setActionsAnchor(null);
      window.cancelAnimationFrame(focusFrame.current);
      focusFrame.current = window.requestAnimationFrame(() => replacement.focus({ preventScroll: true }));
    } else {
      (replacement ?? triggerRef.current)?.focus({ preventScroll: true });
    }
    displacedFocus.current = null;
  });
  useEffect(() => () => window.cancelAnimationFrame(focusFrame.current), []);
  const titleDividerStyles = {
    borderInlineStart: { xs: 0, md: `1px solid ${designTokens.color.divider}` },
    paddingInlineStart: { xs: 0, md: `${designTokens.space.lg}px` },
  };
  const firstLineHeight = {
    xs: `${designTokens.layout.pageHeaderFirstLineHeightCompact}px`,
    sm: `${designTokens.layout.pageHeaderFirstLineHeight}px`,
  };

  useEffect(() => {
    if (!hasMenuContent) setActionsAnchor(null);
  }, [hasMenuContent]);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    const frame = window.requestAnimationFrame(() => {
      menuActionsRef.current
        ?.querySelector<HTMLElement>('button:not(:disabled), input:not(:disabled), [role="combobox"]')
        ?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [actionsOpen]);

  const openActions = (event: MouseEvent<HTMLButtonElement>) => setActionsAnchor(event.currentTarget);
  const closeActions = () => setActionsAnchor(null);

  return (
    <Box data-testid="page-header" sx={{
      flexShrink: 0,
      bgcolor: designTokens.color.pageCanvas,
      color: designTokens.color.primaryText,
      borderBottom: `1px solid ${designTokens.color.divider}`,
      '--app-page-heading': designTokens.color.primaryText,
      '--app-page-supporting-text': designTokens.color.secondaryText,
      '--app-page-error-text': designTokens.color.dangerText,
    }}>
      {actionItems.length > 0 && <Box aria-hidden="true" inert sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', width: 0, height: 0, overflow: 'hidden' }}>
        <Stack ref={measurementRef} direction="row" sx={{ width: 'max-content' }}>
          {actionItems.map(item => <Button component="span" tabIndex={-1} key={item.id} variant="weak" startIcon={item.icon}>{item.label}</Button>)}
        </Stack>
      </Box>}
      <Stack
        ref={headerRef}
        data-testid="page-header-inner"
        direction="row"
        sx={{
          ...pageContentFrame(contentWidth),
          pt: {
            xs: `${designTokens.layout.pageHeaderInsetTopCompact}px`,
            md: `${designTokens.layout.pageHeaderInsetTop}px`,
          },
          pb: `${designTokens.layout.pageHeaderInsetBottom}px`,
          minHeight: `${designTokens.layout.pageHeaderMinHeight}px`,
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: editActions || controls ? 'wrap' : undefined,
          ...(hasHeaderTools ? {
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'auto minmax(0, 1fr) auto' },
            gridTemplateAreas: {
              xs: tabs
                ? titleWidth === 'wide'
                  ? controls ? '"title title" "tabs actions" "controls controls"' : '"title title" "tabs actions"'
                  : controls ? '"title actions" "tabs tabs" "controls controls"' : '"title actions" "tabs tabs"'
                : '"title actions" "controls controls"',
              md: tabs
                ? controls ? '"title tabs actions" "controls controls controls"' : '"title tabs actions"'
                : '"title controls actions"',
            },
            columnGap: `${designTokens.space.lg}px`,
            rowGap: `${designTokens.space.sm}px`,
            alignItems: 'center',
          } : {}),
        }}
      >
        <Stack direction="row" spacing={1} sx={{ flex: editActions ? { xs: '1 1 0', sm: `1 1 ${designTokens.layout.pageHeaderTitleBasis}px` } : 1, minWidth: 0, alignItems: 'flex-start', ...(hasHeaderTools ? { gridArea: 'title', alignItems: 'center', maxWidth: { md: `${designTokens.layout.pageHeaderTitleBasis * (titleWidth === 'wide' ? 2 : 1)}px` } } : {}) }}>
          {navigation ? <Box data-testid="page-header-navigation" sx={{
            minHeight: firstLineHeight,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}>{navigation}</Box> : null}
          <Box sx={{
            flex: 1, minWidth: 0,
            ...(help !== undefined || (refreshAction && !mobile) ? { display: 'flex', alignItems: hasHeaderTools ? 'center' : 'flex-start', gap: 0.5 } : {}),
          }}>
            <Typography
              ref={titleRef}
              tabIndex={titleRef ? -1 : undefined}
              aria-label={titleLabel}
              component="h1"
              variant="h4"
              sx={{
                minHeight: firstLineHeight,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: titleIcon ? `${designTokens.space.xs}px` : undefined,
                ...designTokens.typography.pageTitle,
                color: 'inherit',
                ...(prominent ? { letterSpacing: '-0.02em' } : undefined),
                ...(titleRef ? { borderRadius: designTokens.radius.control, '&:focus-visible': createFocusRingStyles() } : undefined),
              }}
            >
              {titleIcon ? <Box component="span" data-testid="page-header-icon" aria-hidden="true" sx={{
                width: '1em', height: firstLineHeight, flexShrink: 0, alignSelf: 'flex-start',
                display: 'grid', placeItems: 'center', color: designTokens.color.secondaryText,
                '& svg': { fontSize: 'inherit', width: '1em', height: '1em' },
              }}>{titleIcon}</Box> : null}
              {plainTitle ? <Box component="span" sx={{
                minWidth: 0,
                maxWidth: '100%',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                overflow: { xs: 'hidden', sm: 'visible' },
                textOverflow: { xs: 'ellipsis', sm: 'clip' },
                overflowWrap: 'anywhere',
              }}>{title}</Box> : title}
            </Typography>
            {help === undefined ? null : <Box sx={{ minHeight: firstLineHeight, display: 'flex', alignItems: 'center', flexShrink: 0 }}><HelpPopover title={titleLabel ?? (plainTitle ? String(title) : '화면 안내')}>{help}</HelpPopover></Box>}
            {refreshAction && !mobile ? <Box sx={{ minHeight: firstLineHeight, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Tooltip title="새로고침">
                <span>
                  <IconButton
                    aria-label="새로고침"
                    loading={refreshAction.loading ?? false}
                    disabled={refreshAction.disabled}
                    onClick={refreshAction.onClick}
                  >
                    <RefreshRounded />
                  </IconButton>
                </span>
              </Tooltip>
            </Box> : null}
          </Box>
        </Stack>
        {tabs ? <Box data-testid="page-navigation" sx={{
          gridArea: 'tabs',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          ...titleDividerStyles,
        }}>
          <PageTabsProvider>{tabs}</PageTabsProvider>
        </Box> : null}
        {controls ? <Box
          data-testid="page-header-controls"
          sx={{
            gridArea: 'controls',
            width: '100%',
            boxSizing: 'border-box',
            ...(!tabs ? titleDividerStyles : {}),
            minWidth: 0,
            minHeight: firstLineHeight,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >{controls}</Box> : null}
        <Stack direction="row" sx={{ display: hasHeaderTools ? 'flex' : 'contents', gridArea: 'actions', minWidth: 0, alignSelf: 'start', alignItems: 'center', gap: 1 }}>
        {utilities && !mobile ? <Stack
          data-testid="page-header-utilities"
          direction="row"
          spacing={1}
          sx={{
            height: firstLineHeight,
            minHeight: firstLineHeight,
            flexShrink: 0,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            alignItems: 'center',
          }}
        >
          {utilities}
        </Stack> : null}
        {(actions && !actionsInMenu) || inlineItems.length > 0 ? <Stack
          data-testid="page-header-actions"
          direction="row"
          spacing={1}
          sx={{
            height: firstLineHeight,
            minHeight: firstLineHeight,
            flexShrink: 0,
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            alignItems: 'center',
          }}
        >
          {actions && !actionsInMenu ? actions : null}
          {inlineItems.map(item => <HeaderActionButton key={item.id} item={item} inline onFocusRemoved={onActionFocusRemoved} />)}
        </Stack> : null}
        {primaryAction && !primaryActionInMenu ? <Stack
          data-testid="page-header-primary-action"
          direction="row"
          sx={{
            minHeight: firstLineHeight,
            flexShrink: 0,
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {primaryAction}
        </Stack> : null}
        {hasMenuContent ? <Box
          data-testid="page-header-actions-rail"
          sx={{ minHeight: firstLineHeight, display: 'flex', alignItems: 'center', flexShrink: 0, [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize } }}
        >
          <Tooltip title={actionsLabel}>
            <IconButton
              ref={triggerRef}
              data-testid="page-header-actions-trigger"
              aria-label={`${actionsLabel} 열기`}
              aria-controls={actionsOpen ? actionsPopoverId : undefined}
              aria-expanded={actionsOpen ? 'true' : undefined}
              aria-haspopup="dialog"
              onClick={(event) => actionsOpen ? closeActions() : openActions(event)}
              sx={{
                border: `1px solid ${designTokens.color.strongDivider}`,
                bgcolor: actionsOpen ? designTokens.color.actionSoft : 'transparent',
                color: designTokens.color.secondaryText,
              }}
            >
              <MoreHorizRounded />
            </IconButton>
          </Tooltip>
          <Popover
            id={actionsPopoverId}
            open={actionsOpen}
            anchorEl={actionsAnchor}
            onClose={closeActions}
            onKeyDown={(event) => {
              if (event.key !== 'Escape') return;
              event.stopPropagation();
              closeActions();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: -designTokens.layout.menuAnchorGap, horizontal: 'right' }}
            keepMounted
            slotProps={{
              paper: {
                role: 'dialog',
                'aria-label': actionsLabel,
                sx: popupSurfaceStyles('menu'),
              },
            }}
          >
            <Stack
              ref={menuActionsRef}
              role="group"
              aria-label={actionsLabel}
              onClick={(event) => {
                if ((event.target as Element).closest('button')) window.setTimeout(closeActions, 0);
              }}
              sx={{
                minWidth: 200,
                maxWidth: `calc(100vw - ${designTokens.layout.popupViewportInset * 2}px)`,
                p: 1,
                alignItems: 'stretch',
                '& > .MuiButton-root, & > .MuiFormControl-root, & > [data-testid="compact-date-field"]': { width: '100%' },
                '& .MuiButton-root': {
                  minHeight: designTokens.layout.pageHeaderControlSize,
                  [designTokens.media.touchTarget]: { minHeight: designTokens.layout.touchTargetMinSize },
                  height: 'auto',
                  paddingInlineStart: `${designTokens.layout.menuItemInlineInset}px`,
                  paddingInlineEnd: `${designTokens.layout.menuItemEndInset}px`,
                  paddingBlock: `${designTokens.layout.menuItemBlockInset}px`,
                  gap: `${designTokens.layout.menuItemGap}px`,
                  justifyContent: 'flex-start',
                  border: 'none',
                  borderRadius: designTokens.radius.menuItem,
                  boxShadow: 'none',
                  bgcolor: 'transparent',
                  color: designTokens.color.primaryText,
                  fontSize: designTokens.typography.ui.fontSize,
                  fontWeight: 400,
                  textAlign: 'left',
                  '&:hover': {
                    border: 'none',
                    boxShadow: 'none',
                    bgcolor: designTokens.color.hoverSurface,
                  },
                  '&.Mui-disabled': {
                    border: 'none',
                    bgcolor: 'transparent',
                    color: designTokens.color.disabledText,
                  },
                  '&.MuiButton-colorError:not(.Mui-disabled)': { color: designTokens.color.dangerText },
                  '&.Mui-focusVisible': { ...createFocusRingStyles({ inset: true }), boxShadow: 'none' },
                },
                '& > .MuiStack-root': { flexDirection: 'column', alignItems: 'stretch', gap: 0 },
              }}
            >
              {primaryActionInMenu ? primaryAction : null}
              {mobile ? utilities : null}
              {mobile && refreshAction ? <Button
                aria-label="새로고침"
                startIcon={<RefreshRounded />}
                loading={refreshAction.loading ?? false}
                disabled={refreshAction.disabled}
                onClick={refreshAction.onClick}
              >새로고침</Button> : null}
              {actionsInMenu ? actions : null}
              {overflowItems.map(item => <HeaderActionButton key={item.id} item={item} onFocusRemoved={onActionFocusRemoved} />)}
            </Stack>
          </Popover>
        </Box> : null}
        {editActions ? <Stack
          data-testid="page-header-edit-actions"
          role="group"
          aria-label="편집 작업"
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            order: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: '100%',
            minWidth: 0,
            minHeight: firstLineHeight,
            ml: 'auto',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              flexShrink: 0,
            },
          }}
        >
          {editActions}
        </Stack> : null}
        </Stack>
      </Stack>
    </Box>
  );
}
